import React, { useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { FaDownload, FaShareAlt } from 'react-icons/fa';
import { shareCanvasImage } from '../utils/download';

/**
 * Renders a QR code entirely in the browser (no external service) and offers a
 * PNG download and (on phones) a native share sheet.
 *
 * Why local rendering: the site's Content-Security-Policy is
 *   img-src 'self' data: blob:
 * which blocks third-party QR image hosts such as api.qrserver.com — every QR
 * that used that service rendered as a broken image. qrcode.react draws the
 * code into a <canvas> (allowed by CSP, and no network round-trip).
 */
interface QrCodeProps {
  /** Text/URL encoded into the QR code. */
  value: string;
  /** On-screen size in pixels. */
  size?: number;
  /** Base file name (no extension) for the PNG download / share. */
  fileName?: string;
  /** Show the PNG download button. */
  showDownload?: boolean;
  /** Show the share button (native share sheet where available). */
  showShare?: boolean;
  /** Caption passed to the share sheet. */
  shareTitle?: string;
  /** Label for the download button. */
  downloadLabel?: string;
  /** Extra classes for the wrapper. */
  className?: string;
}

const QrCode: React.FC<QrCodeProps> = ({
  value,
  size = 192,
  fileName = 'qr-code',
  showDownload = true,
  showShare = false,
  shareTitle = 'Scan this QR code',
  downloadLabel = 'Download QR',
  className = '',
}) => {
  const holderRef = useRef<HTMLDivElement>(null);

  const getCanvas = (): HTMLCanvasElement | null =>
    holderRef.current?.querySelector('canvas') || null;

  const handleDownload = () => {
    const canvas = getCanvas();
    if (!canvas) return;
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = fileName.toLowerCase().endsWith('.png') ? fileName : `${fileName}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleShare = async () => {
    const canvas = getCanvas();
    if (!canvas) return;
    await shareCanvasImage(canvas, fileName, shareTitle);
  };

  return (
    <div className={`flex flex-col items-center ${className}`}>
      <div
        ref={holderRef}
        className='inline-flex bg-white p-2 rounded-xl border border-slate-200 shadow-md'
      >
        <QRCodeCanvas
          value={value || ' '}
          size={size * 2}
          style={{ width: size, height: size }}
          bgColor='#ffffff'
          fgColor='#000000'
          level='M'
          includeMargin
        />
      </div>

      {(showDownload || showShare) && (
        <div className='mt-3 flex flex-wrap items-center justify-center gap-2'>
          {showDownload && (
            <button
              type='button'
              onClick={handleDownload}
              className='inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700 transition-colors'
            >
              <FaDownload /> {downloadLabel}
            </button>
          )}
          {showShare && (
            <button
              type='button'
              onClick={handleShare}
              className='inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors'
            >
              <FaShareAlt /> Share
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default QrCode;
